/**
 * LoginPage — Central Auth Redirect
 *
 * This page does NOT render a local login form.
 * Authentication is handled exclusively by Central Identity.
 *
 * Behavior:
 * 1. If URL contains OIDC callback params (code + state) → handle token exchange
 * 2. If URL contains error param → show error with retry button (prevent redirect loop)
 * 3. If already authenticated → redirect to dashboard
 * 4. Otherwise → immediately redirect to Central OIDC authorize
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCentralAuth } from '../hooks/useCentralAuth';
import {
  clearPostLoginPath,
  peekPostLoginPath,
  resolvePostLoginPath,
} from '../services/oidc/returnPath';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, initiateLogin } = useCentralAuth();
  const [showManualRetry, setShowManualRetry] = useState(false);

  // Read directly from window.location so replaceState is reflected immediately.
  // useSearchParams lags behind window.history.replaceState by one render cycle,
  // causing a spurious initiateLogin() call after the OIDC callback clears the URL.
  const rawParams = new URLSearchParams(window.location.search);
  const hasOidcCallback = rawParams.has('code') && rawParams.has('state');
  const errorParam = rawParams.get('error');

  // The original deep link. After the full-page OIDC round trip there is no
  // router state, so the path the callback restored comes first; AuthGuard's
  // in-app state and an explicit ?returnTo= follow. Every candidate is
  // validated as a same-origin relative path (services/oidc/returnPath.ts).
  const from = resolvePostLoginPath({
    callbackPath: peekPostLoginPath(),
    routerFrom: (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from,
    returnToParam: rawParams.get('returnTo'),
  });

  useEffect(() => {
    // Don't redirect if there's an error (prevent loop)
    if (errorParam) return;
    // Don't initiate a NEW login while OIDC callback is being processed
    if (hasOidcCallback) return;
    // Don't redirect while authenticating
    if (isLoading) return;

    // Already authenticated → go to original destination
    if (isAuthenticated) {
      clearPostLoginPath();
      navigate(from, { replace: true });
      return;
    }

    // Not authenticated and no callback in progress → redirect to Central OIDC immediately.
    // No need to wait for isInitialized: if there's no code+state in the URL there's nothing
    // to initialize first, and waiting causes a visible spinner flash before the redirect.
    initiateLogin(from).catch((err) => {
      console.error('[Login] initiateLogin failed:', err);
    });

    // Safety net: if the redirect hasn't happened within 3s, show a manual
    // button so the user isn't stranded on the spinner.
    const timer = window.setTimeout(() => setShowManualRetry(true), 3000);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated, isLoading, hasOidcCallback, errorParam, from, navigate, initiateLogin]);

  // Error state: show message with retry (prevents infinite redirect loop)
  if (errorParam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <h1 className="text-3xl font-bold text-primary-600">BeHeard</h1>
          <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
            {decodeURIComponent(errorParam)}
          </div>
          <button
            onClick={() => initiateLogin()}
            className="btn-primary w-full py-3"
          >
            Try again with LevelUp Central
          </button>
        </div>
      </div>
    );
  }

  // Default: show redirect spinner (with manual retry fallback after 3s)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center space-y-4 max-w-md px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
        <p className="text-gray-500">Redirecting to LevelUp Central...</p>
        {showManualRetry && (
          <div className="pt-4 border-t border-gray-200 space-y-3">
            <p className="text-xs text-gray-400">
              Taking longer than expected. Try clearing stale auth state and signing in manually.
            </p>
            <button
              onClick={() => {
                try { sessionStorage.clear(); } catch { /* ignore */ }
                void initiateLogin(from);
              }}
              className="btn-primary w-full py-2 text-sm"
            >
              Sign in with LevelUp Central
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
